<script setup lang="ts">
import { computed, ref, watch } from 'vue'

const props = defineProps<{
  modelValue: boolean
  currentLocation: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'select-location': [location: string]
  'clear-location': []
}>()

const locationSearchQuery = ref('')
const isLocationSearching = ref(false)
const locationSearchError = ref('')
const locationSearchResults = ref<string[]>([])
const demoLocationSuggestions = [
  'Anaheim, CA',
  'Los Angeles, CA',
  'Irvine, CA',
  'San Diego, CA',
  'Yosemite National Park, CA',
  'Las Vegas, NV',
] as const

const filteredDemoLocationSuggestions = computed(() => {
  const query = locationSearchQuery.value.trim().toLowerCase()
  if (!query) return [...demoLocationSuggestions]
  return demoLocationSuggestions.filter((location) => location.toLowerCase().includes(query))
})

watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) return
    locationSearchQuery.value = props.currentLocation || ''
    locationSearchError.value = ''
    locationSearchResults.value = []
  },
)

function closeModal() {
  emit('update:modelValue', false)
}

function pickLocation(location: string) {
  emit('select-location', location)
  closeModal()
}

function clearLocation() {
  emit('clear-location')
  closeModal()
}

async function searchLocationsOnline() {
  const query = locationSearchQuery.value.trim()
  if (!query) {
    locationSearchResults.value = []
    locationSearchError.value = 'Enter a location to search.'
    return
  }

  isLocationSearching.value = true
  locationSearchError.value = ''

  try {
    // Free demo lookup using OpenStreetMap Nominatim.
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=6&q=${encodeURIComponent(query)}`,
    )

    if (!response.ok) {
      throw new Error('Search failed')
    }

    const data = (await response.json()) as Array<{ display_name?: string }>
    const uniqueResults = Array.from(
      new Set(data.map((entry) => entry.display_name?.trim()).filter(Boolean)),
    ) as string[]

    locationSearchResults.value = uniqueResults
    if (!uniqueResults.length) {
      locationSearchError.value = 'No results found. Try a broader location.'
    }
  } catch {
    locationSearchResults.value = []
    locationSearchError.value =
      'Location search is unavailable right now. Use a quick pick for the demo.'
  } finally {
    isLocationSearching.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <div class="modal modal-bottom sm:modal-middle" :class="{ 'modal-open': modelValue }">
      <div class="modal-box">
        <h3 class="font-semibold mb-3">Pick Location</h3>
        <form class="flex gap-2 mb-3" @submit.prevent="searchLocationsOnline">
          <input
            v-model="locationSearchQuery"
            type="text"
            class="input input-sm input-bordered grow"
            placeholder="Search location"
          />
          <button type="submit" class="btn btn-sm btn-primary" :disabled="isLocationSearching">
            {{ isLocationSearching ? 'Searching...' : 'Search' }}
          </button>
        </form>

        <div class="mb-3">
          <div class="text-xs uppercase tracking-wide opacity-70 mb-1">Quick Picks</div>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="location in filteredDemoLocationSuggestions"
              :key="location"
              type="button"
              class="badge badge-outline badge-primary py-3 px-3"
              @click="pickLocation(location)"
            >
              {{ location }}
            </button>
          </div>
        </div>

        <div v-if="locationSearchResults.length > 0" class="mb-3">
          <div class="text-xs uppercase tracking-wide opacity-70 mb-1">Search Results</div>
          <ul class="menu bg-base-200 rounded-box p-1">
            <li v-for="result in locationSearchResults" :key="result">
              <button type="button" class="text-left" @click="pickLocation(result)">
                {{ result }}
              </button>
            </li>
          </ul>
        </div>

        <p v-if="locationSearchError" class="text-xs text-warning mb-2">
          {{ locationSearchError }}
        </p>

        <div class="modal-action mt-4">
          <button
            v-if="currentLocation"
            type="button"
            class="btn btn-ghost btn-sm mr-auto"
            @click="clearLocation"
          >
            Clear Location
          </button>
          <button type="button" class="btn btn-sm" @click="closeModal">Done</button>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop" @submit.prevent="closeModal">
        <button @click="closeModal">close</button>
      </form>
    </div>
  </Teleport>
</template>
